import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { MessagingProducts } from '~/types';
import { User } from '~/schemas/user';

// Enable Map and Set with Immer
enableMapSet();

// Define the activity record types to match UserActivity
export interface ActivityRecord {
  _id: string;
  id: string;
  activity_type: string;
  person_id: string;
  agent_id: string;
  created_at: string;
  updated_at: string;
  ended_at: string;
  messaging_product: any;
  messaging_type?: MessagingProducts;
  typeOfComment?: string;
  person_channel_id?: string;
  comment?: string;
  isHidden?: boolean;
  message?: string;
  actorType?: actorType;
  reopen?: boolean;
  tokens: Tokens;
  media?: {
    url: string;
    type: string;
    caption?: string;
    fileName?: string;
    file?: File;
    origin?: string;
  };
  user?: User;
  [key: string]: any;
}

// Define message type matching schemas/message.ts (adjust if needed)
export enum actorType {
  BOT = 'BOT',
  USER = 'USER',
  AGENT = 'AGENT',
}

// Ensure Tokens type matches the required schema, potentially non-optional
interface Tokens {
  model?: string;
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
}

interface ActivityRecordsState {
  items: Map<string, ActivityRecord>;
  loadedActivities: Set<string>;
}

const initialState: ActivityRecordsState = {
  items: new Map(),
  loadedActivities: new Set(),
};

// Updated helper to provide default empty string for ended_at
const ensureRequiredFields = (
  record: Partial<ActivityRecord>,
): ActivityRecord => {
  const defaults = {
    messaging_product: null,
    tokens: {},
    ended_at: '', // Default to empty string instead of null
    // Add defaults for other required fields if needed
    id: '',
    activity_type: '',
    person_id: '',
    agent_id: '',
    created_at: '',
    updated_at: '',
  };
  // Ensure _id is present before casting
  if (!record._id) {
    console.error('Attempted to process ActivityRecord without _id:', record);
    // Return a dummy object or throw error, depending on desired handling
    // This example returns a partially filled object to avoid crashing, but logs error
    return {
      ...defaults,
      _id: `invalid-${Date.now()}`,
      ...record,
    } as ActivityRecord;
  }
  return {
    ...defaults,
    ...record,
  } as ActivityRecord; // Cast, assuming _id is present now
};

const activityRecordsSlice = createSlice({
  name: 'activityRecords',
  initialState,
  reducers: {
    addActivityRecords: (
      state,
      action: PayloadAction<{
        records: Partial<ActivityRecord>[];
        activityId: string;
      }>,
    ) => {
      const { records, activityId } = action.payload;
      records.forEach((partialRecord) => {
        if (partialRecord._id) {
          const ensuredRecord = ensureRequiredFields(partialRecord);
          state.items.set(ensuredRecord._id, ensuredRecord);
        }
      });
      state.loadedActivities.add(activityId);
    },
    updateActivityRecord: (
      state,
      action: PayloadAction<Partial<ActivityRecord>>,
    ) => {
      const partialRecord = action.payload;
      if (partialRecord._id) {
        const ensuredRecord = ensureRequiredFields(partialRecord);
        state.items.set(ensuredRecord._id, ensuredRecord);
      }
    },
    addActivityRecord: (
      state,
      action: PayloadAction<Partial<ActivityRecord>>,
    ) => {
      const partialRecord = action.payload;
      if (partialRecord._id) {
        const ensuredRecord = ensureRequiredFields(partialRecord);
        state.items.set(ensuredRecord._id, ensuredRecord);
        if (partialRecord.person_id && partialRecord.agent_id) {
          state.loadedActivities.add(
            `${partialRecord.person_id}-${partialRecord.agent_id}`,
          );
        }
      }
    },
    clearActivityData: (state, action: PayloadAction<string>) => {
      const activityId = action.payload;
      state.loadedActivities.delete(activityId);
    },
  },
});

export const {
  addActivityRecords,
  updateActivityRecord,
  addActivityRecord,
  clearActivityData,
} = activityRecordsSlice.actions;

// Define RootState type
export interface RootState {
  activityRecords: ActivityRecordsState;
}

// Selectors
const selectActivityRecordsMap = (state: RootState) =>
  state.activityRecords.items;
const selectPersonId = (_state: RootState, personId: string | undefined) =>
  personId;
const selectAgentId = (
  _state: RootState,
  _personId: string | undefined,
  agentId: string | undefined,
) => agentId;

const EMPTY_ACTIVITY_RECORDS: ActivityRecord[] = [];

// Memoized selector for activity records by person and agent ID
export const selectActivityRecordsByActivityId = createSelector(
  [selectActivityRecordsMap, selectPersonId, selectAgentId],
  (recordsMap, personId, agentId): ActivityRecord[] => {
    if (!personId || !agentId) {
      return EMPTY_ACTIVITY_RECORDS;
    }

    const filtered = Array.from(recordsMap.values()).filter(
      (record) => record.person_id === personId && record.agent_id === agentId,
    );

    if (filtered.length === 0) {
      return EMPTY_ACTIVITY_RECORDS;
    }

    // Sort the filtered results
    return filtered.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  },
);

export const selectIsActivityLoaded = (
  state: RootState,
  activityId: string,
) => {
  return state.activityRecords.loadedActivities.has(activityId);
};

export default activityRecordsSlice.reducer;
